import React, { useState, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { BarChart3, ChevronsLeft, ChevronsRight, Eye } from "lucide-react";
import ChartModal from "../user/static-charts/ChartModal"; // Assuming this path is correct
import AppContext from "../../../context/AppContext"; // Assuming this path is correct
import translations from "../../common/Translations";

const BarChart = ({
  tempData = { graphValueList: [] },
  chartName = "Default Chart Name",
}) => {
  const { theme,langMode } = useContext(AppContext);
  const isRtl = langMode === "ar";
  const [showModal, setShowModal] = useState(false);
  const [chartView, setChartView] = useState("radar"); // Default to bar chart for "graph"
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3; // Number of bars/data points to display per page
  const totalBars = tempData?.graphValueList?.length || 0;
  const maxPage = Math.ceil(totalBars / itemsPerPage);

  const paginatedCategories =
    tempData?.graphValueList
      ?.slice(
        currentPage * itemsPerPage,
        Math.min(totalBars, (currentPage + 1) * itemsPerPage)
      )
      .map((item) => item.value || "Unanswered") || [];

  const paginatedData =
    tempData?.graphValueList
      ?.slice(
        currentPage * itemsPerPage,
        Math.min(totalBars, (currentPage + 1) * itemsPerPage)
      )
      .map((item) => item.frequency) || [];

  // Modern color palettes
  const defaultColors = [ "#D97706","#E879F9","#7C3AED", "#059669", "#4F46E5",  "#DC2626", "#0891B2",]; // Deeper Indigo, Green, Orange, Red, Violet, Cyan, Pink
  const darkThemeColors = ["#818CF8", "#34D399", "#FBBF24", "#F87171", "#C4B5FD", "#67E8F9", "#F472B6"]; // Lighter, more vibrant shades for dark theme
  const highContrastColors = ["#00FFFF", "#FF00FF", "#FFFF00", "#00FF00", "#FFD700", "#C0C0C0", "#FFA07A"]; // Bold and distinct for high contrast

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const baseChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeout', // Smoother easing
        speed: 700,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 300
        }
      },
      foreColor: theme === "dark" ? "#CBD5E1" : theme === "high-contrast" ? "#FFF" : "#374151", // Global text color
      fontFamily: 'MediumFont, Manrope, sans-serif', // Modern font
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "13px",
        fontWeight: 600,
        colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#E2E8F0" : "#374151")], // Data label color
      },
      background: {
        enabled: true,
        foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
        padding: 6,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme === "dark" ? "#4B5563" : "#D1D5DB",
        opacity: 0.9,
      },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "14px",
      labels: {
        colors: theme === "dark" ? "#CBD5E1" : theme === "high-contrast" ? "#FFF" : "#374151", // Legend label color
      },
      itemMargin: {
        horizontal: 12,
        vertical: 6
      },
      markers: {
        radius: 12, // Rounded markers
      }
    },
    tooltip: {
      enabled: true,
      theme: theme === "dark" ? "dark" : "light",
      style: {
        fontSize: "14px",
        fontFamily: "MediumFont, Manrope, sans-serif",
      },
      y: {
        formatter: function (val) {
          return `${val} responses`; // More descriptive tooltip
        }
      },
      x: {
        formatter: function (val) {
          return val; // Display the full category name in tooltip
        }
      }
    },
    colors: getChartColors(), // Dynamic colors
    grid: {
      borderColor: theme === "dark" ? "#374151" : "#E5E7EB", // Lighter, more subtle grid lines
      strokeDashArray: 3, // Slightly dashed grid lines
      position: 'back',
      xaxis: {
        lines: {
          show: false // Typically hide vertical grid lines for cleaner look
        }
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  const ChartView = () => (
    <ReactApexChart
      options={{
        ...baseChartOptions,
        labels: paginatedCategories,
        plotOptions: {
          pie: {
            donut: {
              size: '65%', // Slightly larger donut hole
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total Responses', // More descriptive total label
                  fontSize: '13px',
                  fontWeight: 600,
                  color: baseChartOptions.chart.foreColor,
                  formatter: function (w) {
                    return w.globals.series.reduce((a, b) => a + b, 0); // Sum of all values
                  }
                },
                value: {
                  fontSize: '14px',
                  color: baseChartOptions.chart.foreColor,
                  formatter: function (val) {
                    return val;
                  }
                }
              },
            },
          },
        },
        // Remove x and y axis for pie/donut charts
        xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { show: false } },
        grid: { show: false }, // No grid for pie/donut
      }}
      series={paginatedData}
      type="donut"
      width="100%"
      height="300" // Fixed height for consistent sizing
    />
  );

  const GraphView = () => (
    <ReactApexChart
      options={{
        ...baseChartOptions,
        chart: {
          ...baseChartOptions.chart,
          type: "bar",
        },
        plotOptions: {
          bar: {
            distributed: true,
            borderRadius: 6, // More rounded bars
            columnWidth: '70%', // Wider bars
            dataLabels: {
              position: 'top',
            },
          },
        },
        xaxis: {
          categories: paginatedCategories,
          labels: {
            style: {
              fontSize: "13px",
              colors: baseChartOptions.chart.foreColor,
            },
            formatter: function (value) {
              return value.length > 20 ? value.substring(0, 17) + "..." : value; // Limit label length slightly more
            },
            offsetY: 0,
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: baseChartOptions.chart.foreColor,
              fontSize: "13px"
            },
            formatter: function (val) {
              return Math.floor(val); // Ensure y-axis labels are integers
            }
          },
          title: {
            text: 'Number of Responses',
            style: {
              color: baseChartOptions.chart.foreColor,
              fontSize: '14px',
              fontWeight: 600
            }
          }
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: {
            formatter: function (val) {
              return `${val} responses`;
            }
          }
        }
      }}
      series={[{ name: 'Responses', data: paginatedData }]} // Added series name
      type="bar"
      width="100%"
      height="300"
    />
  );

  const AreaChartView = () => (
    <ReactApexChart
      options={{
        ...baseChartOptions,
        chart: {
          ...baseChartOptions.chart,
          type: "area",
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.6, // Slightly less opaque fill
            opacityTo: 0.8,
            stops: [0, 90, 100],
            colorStops: getChartColors().map(color => ({
              offset: 0, color: color, opacity: 0.6
            })),
          }
        },
        xaxis: {
          categories: paginatedCategories,
          labels: {
            style: {
              fontSize: "13px",
              colors: baseChartOptions.chart.foreColor,
            },
            formatter: function (value) {
              return value ? (value.length > 20 ? value.substring(0, 17) + "..." : value) : "N/A";
            },
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: baseChartOptions.chart.foreColor,
              fontSize: "13px"
            },
            formatter: function (val) {
              return Math.floor(val);
            }
          },
          title: {
            text: 'Number of Responses',
            style: {
              color: baseChartOptions.chart.foreColor,
              fontSize: '14px',
              fontWeight: 600
            }
          }
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: {
            formatter: function (val) {
              return `${val} responses`;
            }
          }
        }
      }}
      series={[{ name: chartName, data: paginatedData }]}
      type="area"
      width="100%"
      height="300"
    />
  );

  const RadarChartView = () => (
    <ReactApexChart
      options={{
        ...baseChartOptions,
        chart: {
          ...baseChartOptions.chart,
          type: "radar",
          dropShadow: { // Subtle shadow for depth
            enabled: true,
            blur: 1,
            left: 1,
            top: 1
          }
        },
        plotOptions: {
          radar: {
            polygons: {
              strokeColors: theme === "dark" ? "#4B5563" : "#D1D5DB", // Border for polygons
              fill: {
                colors: theme === "dark" ? ["#374151", "#1F2937"] : ["#F9FAFB", "#F3F4F6"] // Gradient fill for polygons
              }
            }
          }
        },
        xaxis: {
          categories: paginatedCategories,
          labels: {
            style: {
              fontSize: "13px",
              colors: baseChartOptions.chart.foreColor,
              fontWeight: 500
            },
            formatter: function (value) {
              return value.length > 15 ? value.substring(0, 12) + "..." : value;
            },
          },
        },
        yaxis: {
          show: false, 
          tickAmount: 5, // A few ticks if needed, but keeping hidden for now
          labels: {
            formatter: function (val) {
              return Math.floor(val);
            }
          }
        },
        grid: {
          show: false
        },
        stroke: {
          width: 2,
          colors: getChartColors() // Use dynamic colors for stroke
        },
        fill: {
          opacity: 0.7
        },
        markers: {
          size: 4,
          colors: getChartColors(),
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 7,
          }
        }
      }}
      series={[{ name: chartName, data: paginatedData }]}
      type="radar"
      width="100%"
      height="300"
    />
  );

  const handleNext = () => {
    if (currentPage < maxPage - 1) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  return (
    <div className={`h-full transition-all duration-300 ease-in-out
      ${theme === 'dark' ? 'bg-gray-800 text-gray-100' :
        theme === 'high-contrast' ? 'bg-black text-white border-2 border-yellow-300' :
          'bg-white text-gray-900'}`}>

      {paginatedData.length === 0 ? (
        <div className="flex h-full min-h-[256px] flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === 'dark'
                ? 'bg-[#236DB4]/20 text-[#236DB4]'
                : theme === 'high-contrast'
                  ? 'bg-black text-yellow-300'
                  : 'bg-[#236DB4]/10 text-[#236DB4]'
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === 'high-contrast' ? 'text-yellow-300' : theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              {translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === 'high-contrast' ? 'text-yellow-200' : 'text-gray-500'}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-base font-semibold ${theme === 'high-contrast' ? 'text-yellow-300' : ''}`}>
              {chartName}
            </h3>
            <button
              type="button"
              className="text-sm font-medium text-center inline-flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
              onClick={() => setShowModal(true)}
              aria-label="View chart options"
            >
              <Eye className="w-5 h-5" />
            </button>
          </div>

          <div className="relative min-h-[300px] flex items-center justify-center"> {/* Consistent height for chart area */}
            {chartView === "chart" ? (
              <ChartView />
            ) : chartView === "area" ? (
              <AreaChartView />
            ) : chartView === "radar" ? (
              <RadarChartView />
            ) : ( // Default to Bar Chart (GraphView)
              <GraphView />
            )}
          </div>
        </>
      )}

      {totalBars > itemsPerPage && (
        <div className={`flex justify-center items-center mt-6 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            className={`p-2 rounded-full mx-3 transition-all duration-200 ease-in-out
              ${theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                : theme === 'high-contrast'
                  ? 'bg-yellow-300 text-black hover:bg-yellow-400 border border-yellow-300'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900'
              }
              disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={handlePrev}
            disabled={currentPage === 0}
            aria-label="Previous page"
          >
            {isRtl ? (
              <ChevronsRight className="w-6 h-6" />
            ) : (
              <ChevronsLeft className="w-6 h-6" />
            )}
          </button>
          <span className={`text-sm font-medium ${theme === 'high-contrast' ? 'text-yellow-300' : 'text-gray-500'}`}>
            Page {currentPage + 1} of {maxPage}
          </span>
          <button
            className={`p-2 rounded-full mx-3 transition-all duration-200 ease-in-out
              ${theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                : theme === 'high-contrast'
                  ? 'bg-yellow-300 text-black hover:bg-yellow-400 border border-yellow-300'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900'
              }
              disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={handleNext}
            disabled={currentPage === maxPage - 1}
            aria-label="Next page"
          >
            {isRtl ? (
              <ChevronsLeft className="w-6 h-6" />
            ) : (
              <ChevronsRight className="w-6 h-6" />
            )}
          </button>
        </div>
      )}
      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default BarChart;
